import { useCallback } from "react";
import type { ApolloCache } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import {
  CurrentUserIssueCountsDocument,
  type EntityType,
  IssueListGroupCountsDocument,
  type IssueRow,
  UpdateIssueAssigneesDocument,
  UpdateIssueTeamsDocument,
} from "#app/lib/data";
import { useMemberEntityResolver } from "#app/lib/data/member-directory-provider";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import {
  buildGroupWhere,
  ISSUE_GROUP_SERVER_KEY,
  UNASSIGNED_GROUP_KEY,
  type IssueGroupDimension,
} from "#app/components/issues/issue-grouping";
import {
  insertIssueIntoGroupCache,
  removeIssueFromGroupCache,
  shiftGroupCounts,
} from "./kanban-cache-utils";
import { optimisticIssue } from "#app/lib/data/optimistic-issue";

// Board drag-reassignment for the membership groupings: dragging a card out of
// one Assignee (or Teams) column into another edits the issue's assignee/team
// LIST — remove the source column's entity, add the target's — where a stage
// drag swaps a single scalar. Because the selectors are `hasSome`, one card can
// sit in several columns at once, and clearing the list (a drop on the
// no-value column) removes it from all of them.

/** The board dimensions a drag can reassign across. */
export type BoardReassignDimension = Extract<
  IssueGroupDimension,
  "assignee" | "teams"
>;

type ReassignParams = {
  issue: IssueRow;
  dimension: BoardReassignDimension;
  /** Key of the column the card was dragged from ({@link UNASSIGNED_GROUP_KEY} for the no-value column). */
  fromKey: string;
  /** Key of the column the card landed on. */
  toKey: string;
};

type ReassignManyParams = Omit<ReassignParams, "issue"> & {
  rows: IssueRow[];
};

/** The fields a reassignment changes, for refreshing any kept row snapshots. */
export type ReassignedIssue = { issueId: string } & Partial<
  Pick<IssueRow, "assignees" | "teams">
>;

// The per-bucket counts and the Only-Me chip live on root fields the column
// list writes never touch — same follow-up the stage mutations use.
const COUNT_REFETCH = {
  refetchQueries: [IssueListGroupCountsDocument, CurrentUserIssueCountsDocument],
};

/**
 * Move the card between the two columns' cached lists and shift the bucket
 * counts, in one optimistic pass. Runs on the optimistic write and again after
 * rollback on the real response, so every step is idempotent: removal filters,
 * insertion dedupes, and `alreadyInTarget` keeps a card that was in the target
 * column all along (a co-assignee's column) from bumping its total.
 */
function applyCacheMove(
  cache: ApolloCache,
  { issue, dimension, fromKey, toKey }: ReassignParams,
) {
  const currentKeys =
    dimension === "assignee"
      ? (issue.assignees ?? []).map((ref) => ref.entityId)
      : (issue.teams ?? []).map((team) => team.teamId);
  const membership = currentKeys.length > 0 ? currentKeys : [UNASSIGNED_GROUP_KEY];
  // A drop on the no-value column clears the whole list, so the card leaves
  // EVERY column it sat in, not just the one it was dragged from.
  const removedKeys = toKey === UNASSIGNED_GROUP_KEY ? membership : [fromKey];
  const alreadyInTarget = membership.includes(toKey);
  const token = (key: string) =>
    JSON.stringify(buildGroupWhere(dimension, key));

  for (const key of removedKeys) {
    removeIssueFromGroupCache(cache, token(key), issue.issueId);
  }
  insertIssueIntoGroupCache(
    cache,
    token(toKey),
    { issueId: issue.issueId, sortOrder: issue.sortOrder },
    undefined,
    alreadyInTarget,
  );
  shiftGroupCounts(cache, ISSUE_GROUP_SERVER_KEY[dimension], [
    ...removedKeys.map((key) => ({ fromKey: key, toKey: undefined })),
    ...(alreadyInTarget ? [] : [{ fromKey: undefined, toKey }]),
  ]);
}

/**
 * Reassignment mutations for a board drag across Assignee or Teams columns.
 * Each drop edits the issue's membership list with a full optimistic response
 * (the card lands in the target column on the drop frame) plus the cache
 * surgery above; the mutation's union errors surface as thrown Errors so one
 * catch at the drag handler covers refusal and transport alike.
 */
export function useBoardReassign() {
  const [updateAssignees] = useMutation(UpdateIssueAssigneesDocument);
  const [updateTeams] = useMutation(UpdateIssueTeamsDocument);
  const { teamMap } = useWorkspaceData();
  const resolveMember = useMemberEntityResolver();

  const reassignAssignee = useCallback(
    async (params: ReassignParams): Promise<ReassignedIssue> => {
      const { issue, fromKey, toKey } = params;
      const withoutFrom = (issue.assignees ?? []).filter(
        (ref) => ref.entityId !== fromKey,
      );
      const targetEntity =
        toKey === UNASSIGNED_GROUP_KEY ? undefined : resolveMember(toKey);
      const target = targetEntity?.kind === "user" ? targetEntity.user : undefined;
      // Kept refs stay in place so the primary (index 0) survives the move;
      // the target lands at the end unless already assigned.
      const nextRefs =
        toKey === UNASSIGNED_GROUP_KEY
          ? []
          : withoutFrom.some((ref) => ref.entityId === toKey)
            ? withoutFrom
            : [
                ...withoutFrom,
                {
                  __typename: "EntityRef" as const,
                  entityType: "user" as EntityType,
                  entityId: toKey,
                  isPrimary: false,
                  user: {
                    __typename: "User" as const,
                    // A workspace member row has no `id`; the userId doubles as
                    // the cache key, matching every optimistic assignee write.
                    id: toKey,
                    userId: toKey,
                    name: target?.name ?? "",
                    email: target?.email ?? "",
                    username: target?.username ?? null,
                    profileImage: target?.profileImage ?? null,
                    avatarColor: target?.avatarColor ?? null,
                    // IssueSummary selects memberStatus — omitting it triggers
                    // Apollo's "Missing field" warning on merge. Selectable
                    // members are active.
                    memberStatus: target?.memberStatus ?? "active",
                  },
                  team: null,
                },
              ];
      const result = await updateAssignees({
        ...COUNT_REFETCH,
        variables: {
          issueId: issue.issueId,
          assignees: nextRefs.map((ref, index) => ({
            entityType: ref.entityType,
            entityId: ref.entityId,
            isPrimary: index === 0,
          })),
        },
        optimisticResponse: {
          updateIssue: optimisticIssue({
            id: issue.id,
            issueId: issue.issueId,
            assignees: nextRefs.map((ref, index) => ({
              ...ref,
              isPrimary: index === 0,
            })),
          }),
        },
        update(cache, { data }) {
          if (data?.updateIssue?.__typename !== "Issue") return;
          applyCacheMove(cache, params);
        },
      });
      const payload = result.data?.updateIssue;
      if (payload?.__typename !== "Issue") {
        throw new Error(payload?.message ?? "Failed to reassign Project");
      }
      return { issueId: payload.issueId, assignees: payload.assignees };
    },
    [updateAssignees, resolveMember],
  );

  const reassignTeam = useCallback(
    async (params: ReassignParams): Promise<ReassignedIssue> => {
      const { issue, fromKey, toKey } = params;
      const withoutFrom = (issue.teams ?? []).filter(
        (team) => team.teamId !== fromKey,
      );
      const target = teamMap.get(toKey);
      const nextTeams =
        toKey === UNASSIGNED_GROUP_KEY
          ? []
          : withoutFrom.some((team) => team.teamId === toKey)
            ? withoutFrom
            : [
                ...withoutFrom,
                {
                  __typename: "Team" as const,
                  id: toKey,
                  teamId: toKey,
                  teamName: target?.teamName ?? "?",
                  icon: {
                    __typename: "Icon" as const,
                    icon: target?.icon?.icon ?? null,
                    color: target?.icon?.color ?? null,
                    percentage: null,
                  },
                },
              ];
      const result = await updateTeams({
        ...COUNT_REFETCH,
        variables: {
          issueId: issue.issueId,
          teamIds: nextTeams.map((team) => team.teamId),
        },
        optimisticResponse: {
          updateIssue: optimisticIssue({
            id: issue.id,
            issueId: issue.issueId,
            teams: nextTeams,
          }),
        },
        update(cache, { data }) {
          if (data?.updateIssue?.__typename !== "Issue") return;
          applyCacheMove(cache, params);
        },
      });
      const payload = result.data?.updateIssue;
      if (payload?.__typename !== "Issue") {
        throw new Error(payload?.message ?? "Failed to reassign Project");
      }
      return { issueId: payload.issueId, teams: payload.teams };
    },
    [updateTeams, teamMap],
  );

  /**
   * Reassign every dragged row, in parallel. Answers the changed fields per
   * row so the caller can refresh any snapshots it keeps (the bulk-selection
   * map). A lone failure rethrows as itself — a permission denial reads better
   * than a tally — while a group failure reports the tally; rows that
   * succeeded keep their moves either way.
   */
  const reassignIssues = useCallback(
    async ({
      rows,
      dimension,
      fromKey,
      toKey,
    }: ReassignManyParams): Promise<ReassignedIssue[]> => {
      const run = dimension === "assignee" ? reassignAssignee : reassignTeam;
      const results = await Promise.allSettled(
        rows.map((issue) => run({ issue, dimension, fromKey, toKey })),
      );
      const failed = results.filter(
        (result): result is PromiseRejectedResult =>
          result.status === "rejected",
      );
      const firstFailure = failed[0];
      if (firstFailure) {
        throw rows.length === 1
          ? firstFailure.reason
          : new Error(
              `${failed.length.toLocaleString()} of ${rows.length.toLocaleString()} Projects failed to update`,
            );
      }
      return results.flatMap((result) =>
        result.status === "fulfilled" ? [result.value] : [],
      );
    },
    [reassignAssignee, reassignTeam],
  );

  return { reassignIssues };
}
